import React, { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

const OverviewPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const menuItems = [
    { id: 'overview', label: '통합 자동화 계측', href: '/overview' },
    { id: 'smart-pbd', label: '스마트 P.B.D. 통합관리', href: '/smart-pbd' },
    { id: 'smart-dcm', label: '스마트 DCM 통합관리', href: '/smart-dcm' },
    { id: 'gnss-settlement', label: '지반침하 GNSS 무선 침하계', href: '/gnss-settlement' }
  ];

  const coreValues = [
    {
      icon: 'ri-flow-chart',
      title: '원스톱 파이프라인',
      description: '수집→정형→분석→경보→보고를 한 플랫폼에서 통합 관리'
    },
    {
      icon: 'ri-sensor-line',
      title: '멀티 센서 호환',
      description: '다양한 센서와 프로토콜을 지원하는 확장 가능한 시스템'
    },
    {
      icon: 'ri-tools-line',
      title: '유지보수 용이',
      description: '모듈형 구조로 간편한 유지보수 및 업그레이드'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: '비용 경쟁력',
      description: '효율적인 시스템 구성으로 운영비용 최적화'
    }
  ];

  const systemFlow = [
    {
      step: '1',
      title: '센서 데이터 수집',
      description: '다양한 센서로부터 실시간 데이터 수집',
      icon: 'ri-sensor-line'
    },
    {
      step: '2',
      title: '로거/게이트웨이',
      description: 'LTE/LoRa 통신을 통한 데이터 전송',
      icon: 'ri-wifi-line'
    },
    {
      step: '3',
      title: '수집/정형/DB',
      description: '데이터 정형화 및 데이터베이스 저장',
      icon: 'ri-database-line'
    },
    {
      step: '4',
      title: '분석 엔진',
      description: '이상치 감지 및 위험도 분석',
      icon: 'ri-brain-line'
    },
    {
      step: '5',
      title: '경보 시스템',
      description: '앱/SMS/E-mail을 통한 즉시 알림',
      icon: 'ri-alarm-warning-line'
    },
    {
      step: '6',
      title: '웹·GIS 표출',
      description: '실시간 모니터링 및 시각화',
      icon: 'ri-dashboard-line'
    }
  ];

  const applicationModules = [
    {
      name: '비탈면(사면)',
      items: ['경사계', '변위계', '간극수압계', '강우량계'],
      description: '사면 안정성 실시간 모니터링',
      image: 'https://public.readdy.ai/ai/img_res/c56456496dfcdcfb0b6ad8efb5564ab6.jpg'
    },
    {
      name: '교량',
      items: ['가속도계', '변형률계', '변위계', '기울기계'],
      description: '교량 구조 건전성 모니터링',
      image: 'https://readdy.ai/api/search-image?query=Bridge%20structural%20health%20monitoring%20system%2C%20accelerometers%20and%20strain%20gauges%20on%20bridge%2C%20blue%20and%20white%20color%20scheme%2C%20professional%20bridge%20monitoring%20equipment&width=400&height=300&seq=bridge-monitoring&orientation=landscape'
    },
    {
      name: '댐/저수지·보',
      items: ['수위계', '침하계', '간극수압계', '누수감지'],
      description: '댐 안전성 및 수위 관리',
      image: 'https://readdy.ai/api/search-image?query=Dam%20and%20reservoir%20monitoring%20system%2C%20water%20level%20sensors%20and%20settlement%20gauges%2C%20blue%20and%20white%20color%20scheme%2C%20professional%20dam%20safety%20monitoring%20equipment&width=400&height=300&seq=dam-monitoring&orientation=landscape'
    },
    {
      name: '제방/방조제',
      items: ['침하계', '경사계', '수위계', '파압계'],
      description: '제방 안정성 및 수위 모니터링',
      image: 'https://readdy.ai/api/search-image?query=Levee%20and%20seawall%20monitoring%20system%2C%20settlement%20and%20water%20level%20monitoring%2C%20blue%20and%20white%20color%20scheme%2C%20professional%20coastal%20protection%20monitoring&width=400&height=300&seq=levee-monitoring&orientation=landscape'
    },
    {
      name: '도시철도·근접시공',
      items: ['진동계', '소음계', '변위계', '경사계'],
      description: '도시철도 및 근접 공사 영향 모니터링',
      image: 'https://readdy.ai/api/search-image?query=Urban%20railway%20and%20construction%20monitoring%20system%2C%20vibration%20and%20noise%20sensors%2C%20blue%20and%20white%20color%20scheme%2C%20professional%20construction%20impact%20monitoring&width=400&height=300&seq=railway-monitoring&orientation=landscape'
    },
    {
      name: '항만·해안',
      items: ['파고계', '조위계', '유속계', '침하계'],
      description: '항만 시설 및 해안 환경 모니터링',
      image: 'https://readdy.ai/api/search-image?query=Port%20and%20coastal%20monitoring%20system%2C%20wave%20height%20and%20tide%20gauges%2C%20blue%20and%20white%20color%20scheme%2C%20professional%20marine%20monitoring%20equipment&width=400&height=300&seq=port-monitoring&orientation=landscape'
    }
  ];

  const alertFeatures = [
    {
      icon: 'ri-alert-line',
      title: '단계별 경보',
      description: '관리기준 연속 초과 기반 단계별 경보 시스템'
    },
    {
      icon: 'ri-notification-line',
      title: '다중 전파',
      description: '앱·문자·메일을 통한 즉시 알림 전파'
    },
    {
      icon: 'ri-cloud-line',
      title: '기상 연동',
      description: '3일 위험도 지도 및 등치선 표출'
    },
    {
      icon: 'ri-shield-check-line',
      title: '신뢰도 향상',
      description: '3σ 이상치·노이즈 제거로 오탐 최소화'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <div className="flex">
        {/* 왼쪽 사이드 메뉴바 */}
        <aside className="w-80 bg-white shadow-lg border-r border-gray-200 min-h-screen sticky top-16">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Product</h2>
            <nav className="space-y-2">
              {menuItems.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  className={`block px-4 py-3 rounded-lg transition-colors cursor-pointer ${
                    activeSection === item.id
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                  }`}
                  onClick={() => setActiveSection(item.id)}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* 메인 콘텐츠 */}
        <main className="flex-1">
          {/* Hero Section */}
          <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
            <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto text-center">
                <h1 className="text-5xl font-bold mb-6">
                  통합 자동화 계측 시스템
                </h1>
                <p className="text-xl mb-8 leading-relaxed">
                  실시간 모니터링·분석·경보·전파를 통한 원스톱 통합 솔루션으로
                  건설 현장의 안전성과 효율성을 극대화합니다.
                </p>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8">
                  <h3 className="text-2xl font-semibold mb-4">핵심 가치</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {coreValues.map((value, index) => (
                      <div key={index} className="text-center">
                        <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                          <i className={`${value.icon} text-2xl`}></i>
                        </div>
                        <h4 className="text-lg font-semibold mb-2">{value.title}</h4>
                        <p className="text-sm opacity-90">{value.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 시스템 플랫폼 */}
          <section className="py-20 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">시스템 플랫폼</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  센서부터 웹 표출까지 완전한 파이프라인을 제공하는 통합 플랫폼
                </p>
              </div>

              {/* 시스템 다이어그램 */}
              <div className="mb-16">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                  <img 
                    src="https://static.readdy.ai/image/62bfd8064e11f74310ce1cc00b80d48a/217dd02402b91243085483670d8217bc.png"
                    alt="EGE 통합자동화 계측 시스템 플랫폼 구조도 - 센서에서 웹 표출까지의 완전한 데이터 파이프라인"
                    title="EGE 통합자동화 계측 시스템 플랫폼"
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                </div>
              </div>

              {/* 데이터 플로우 단계 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center relative border border-blue-200">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-sensor-line text-white text-2xl"></i>
                  </div>
                  <div className="absolute top-4 right-4 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">데이터 수집</h3>
                  <p className="text-gray-600 mb-3">측정센서를 통한 실시간 데이터 수집</p>
                  <div className="text-sm text-blue-600 font-medium">
                    유무선센서 → 측정값 취득
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 text-center relative border border-green-200">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-database-line text-white text-2xl"></i>
                  </div>
                  <div className="absolute top-4 right-4 w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">데이터 가공</h3>
                  <p className="text-gray-600 mb-3">데이터 정형화 및 통계처리</p>
                  <div className="text-sm text-green-600 font-medium">
                    데이터 정형화 → 통계처리 → 이상 상황 판단
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 text-center relative border border-orange-200">
                  <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-alarm-warning-line text-white text-2xl"></i>
                  </div>
                  <div className="absolute top-4 right-4 w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">분석 및 경고</h3>
                  <p className="text-gray-600 mb-3">관리기준 비교 및 대응 전파</p>
                  <div className="text-sm text-orange-600 font-medium">
                    대응 사무소 → 경보 전파 → 정보 전달
                  </div>
                </div>
              </div>

              {/* 시스템 구성 요소 */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center">시스템 구성 요소</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <i className="ri-wifi-line text-white text-xl"></i>
                    </div>
                    <h4 className="font-semibold text-gray-800 mb-2">로거/게이트웨이</h4>
                    <p className="text-sm text-gray-600">LTE/LoRa 통신을 통한 데이터 전송</p>
                  </div>

                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <i className="ri-smartphone-line text-white text-xl"></i>
                    </div>
                    <h4 className="font-semibold text-gray-800 mb-2">모바일 앱</h4>
                    <p className="text-sm text-gray-600">현장에서 실시간 모니터링</p>
                  </div>

                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <i className="ri-file-text-line text-white text-xl"></i>
                    </div>
                    <h4 className="font-semibold text-gray-800 mb-2">표준 리포트 자동화</h4>
                    <p className="text-sm text-gray-600">자동 보고서 생성 및 배포</p>
                  </div>

                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <i className="ri-shield-user-line text-white text-xl"></i>
                    </div>
                    <h4 className="font-semibold text-gray-800 mb-2">데이터 보안</h4>
                    <p className="text-sm text-gray-600">조작 방지 및 권한 관리</p>
                  </div>
                </div>
              </div>

              {/* 핵심 가치 */}
              <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <i className="ri-flow-chart text-blue-600 text-xl"></i>
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">원스톱 통합</h4>
                  <p className="text-sm text-gray-600">수집→정형→분석→경보→보고를 한 플랫폼에서</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-green-600 text-xl"></i>
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">신뢰도 향상</h4>
                  <p className="text-sm text-gray-600">3σ 이상치·노이즈 제거와 기상 연동 위험도로 오탐을 낮춤</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <i className="ri-expand-diagonal-line text-purple-600 text-xl"></i>
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">현장 확장성</h4>
                  <p className="text-sm text-gray-600">멀티 센서·프로토콜 호환, MUX 256ch+ 확장, GNSS 무선 침하</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <i className="ri-notification-line text-orange-600 text-xl"></i>
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">즉시 대응</h4>
                  <p className="text-sm text-gray-600">기준 연속 초과 시 단계 경보—앱·문자·메일로 신속 전파</p>
                </div>
              </div>
            </div>
          </section>

          {/* 핵심 기술 */}
          <section className="py-20 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                  핵심 기술
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  EGE만의 차별화된 기술력으로 최고 수준의 계측 정확도를 실현합니다
                </p>
              </div>

              {/* 계측자료 이상치 검출 및 경보 필터링 기술 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
                <div>
                  <h3 className="text-3xl font-bold text-gray-800 mb-6">
                    계측자료 이상치 검출 및 경보 필터링 기술
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-4 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">3σ 이상치 검출</h4>
                        <p className="text-gray-600">통계적 기법을 활용한 정확한 이상치 판별</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-4 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">오탐 방지</h4>
                        <p className="text-gray-600">불필요한 경보 발생을 최소화하여 신뢰성 향상</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-4 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">지능형 경보 시스템</h4>
                        <p className="text-gray-600">기준치 초과 시에만 정확한 경보 발생</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div 
                  className="h-96 bg-cover bg-center rounded-lg shadow-lg"
                  style={{ backgroundImage: `url('https://static.readdy.ai/image/62bfd8064e11f74310ce1cc00b80d48a/b9d5109f2ad073160e5313790e91f8a2.png')` }}
                ></div>
              </div>

              {/* 센서 노이즈 필터링 기술 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="lg:order-2">
                  <h3 className="text-3xl font-bold text-gray-800 mb-6">
                    센서 노이즈 필터링 기술
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center mr-4 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">AC 노이즈 제거</h4>
                        <p className="text-gray-600">교류전류의 60Hz 노이즈 차단으로 신호 품질 향상</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center mr-4 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">전압 노이즈 필터</h4>
                        <p className="text-gray-600">전압, 전자기, 아답터 등에서 발생하는 노이즈 제거</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center mr-4 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">외부 전기적 충격 대응</h4>
                        <p className="text-gray-600">낙뢰, 외부 누전 등으로 인한 오측 제거</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lg:order-1">
                  <div 
                    className="h-96 bg-cover bg-center rounded-lg shadow-lg"
                    style={{ backgroundImage: `url('https://static.readdy.ai/image/62bfd8064e11f74310ce1cc00b80d48a/0d8240c65fa955c0e97f44de776323c8.png')` }}
                  ></div>
                </div>
              </div>

              {/* 기술적 우위 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
                <div className="bg-blue-50 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">오측 최소화</h3>
                  <p className="text-gray-600">고도화된 필터링 기술로 측정 오차 최소화</p>
                </div>
                <div className="bg-green-50 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-brain-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">지능형 분석</h3>
                  <p className="text-gray-600">AI 기반 패턴 분석으로 정확한 이상 감지</p>
                </div>
                <div className="bg-orange-50 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-time-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">실시간 처리</h3>
                  <p className="text-gray-600">즉시 데이터 처리 및 신속한 대응 체계</p>
                </div>
              </div>
            </div>
          </section>

          {/* 장비 호환성 */}
          <section className="py-20 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                  장비 호환성
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  폭넓은 인터페이스 지원으로 기존 장비와의 완벽한 호환성 제공
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
                <div>
                  <h3 className="text-3xl font-bold text-gray-800 mb-6">
                    다양한 인터페이스 지원
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-4 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">아날로그 신호</h4>
                        <p className="text-gray-600">전압/전류 신호 처리 및 변환</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-4 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">디지털 통신</h4>
                        <p className="text-gray-600">SDI-12/RS232/RS485 프로토콜 지원</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-4 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">펄스 신호</h4>
                        <p className="text-gray-600">펄스 카운터 및 주파수 측정</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-4 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">확장성</h4>
                        <p className="text-gray-600">MUX를 통한 256채널+ 확장 가능</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div 
                  className="h-96 bg-cover bg-center rounded-lg shadow-lg"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=Multi-channel%20data%20acquisition%20system%20with%20various%20sensor%20interfaces%2C%20industrial%20measurement%20equipment%2C%20blue%20and%20white%20color%20scheme%2C%20professional%20monitoring%20hardware%20setup&width=600&height=600&seq=equipment-compatibility&orientation=squarish')`
                  }}
                ></div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">GNSS 정밀 계측 연동</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-satellite-line text-white text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">수 mm급 정밀도</h4>
                    <p className="text-gray-600">RTK-GNSS 기반 고정밀 측위</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-time-line text-white text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">실시간/주기 통계</h4>
                    <p className="text-gray-600">10분 단위 주기적 데이터 수집</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-line-chart-line text-white text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">잔차 관리</h4>
                    <p className="text-gray-600">초기치 적용 및 잔차 분석</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 경보 전파 시스템 */}
          <section className="py-20 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                  경보 전파 시스템
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  즉시 대응을 위한 지능형 경보 시스템으로 신속한 위험 상황 전파
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
                {alertFeatures.map((feature, index) => (
                  <div key={index} className="bg-red-50 rounded-xl p-6 text-center">
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className={`${feature.icon} text-white text-2xl`}></i>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-xl p-8">
                <h3 className="text-2xl font-bold mb-6 text-center">경보 프로세스</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl font-bold">1</span>
                    </div>
                    <h4 className="font-semibold mb-2">기준 초과 감지</h4>
                    <p className="text-sm opacity-90">관리기준 연속 초과 시 자동 감지</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl font-bold">2</span>
                    </div>
                    <h4 className="font-semibold mb-2">단계별 분류</h4>
                    <p className="text-sm opacity-90">위험도에 따른 단계별 경보 분류</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl font-bold">3</span>
                    </div>
                    <h4 className="font-semibold mb-2">다중 전파</h4>
                    <p className="text-sm opacity-90">앱·문자·메일 동시 전송</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl font-bold">4</span>
                    </div>
                    <h4 className="font-semibold mb-2">즉시 대응</h4>
                    <p className="text-sm opacity-90">현장 담당자 즉시 대응 조치</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 적용 모듈 */}
          <section className="py-20 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                  적용 모듈
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  다양한 건설 현장과 인프라에 특화된 모니터링 솔루션
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {applicationModules.map((module, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                    <div 
                      className="h-48 bg-cover bg-center"
                      style={{ backgroundImage: `url(${module.image})` }}
                    ></div>
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">
                        {module.name}
                      </h3>
                      <p className="text-gray-600 mb-4">{module.description}</p>
                      <div className="space-y-2">
                        <h4 className="font-medium text-gray-800">주요 계측항목:</h4>
                        <div className="flex flex-wrap gap-2">
                          {module.items.map((item, idx) => (
                            <span key={idx} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-16 bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">기대 효과</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-search-eye-line text-white text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">조기 검지</h4>
                    <p className="text-gray-600">위험 상황의 조기 감지로 사고 예방</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-speed-line text-white text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">대응 속도 향상</h4>
                    <p className="text-gray-600">실시간 알림으로 신속한 대응 가능</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-map-line text-white text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">GIS 시각화</h4>
                    <p className="text-gray-600">지도 기반 직관적인 현황 파악</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 분석/AI 연계 */}
          <section className="py-20 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                  분석/AI 연계
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  고도화된 데이터 분석과 AI 기술로 정확하고 신뢰할 수 있는 모니터링
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
                <div>
                  <h3 className="text-3xl font-bold text-gray-800 mb-6">
                    지능형 데이터 분석
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center mr-4 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">3σ 이상치 필터링</h4>
                        <p className="text-gray-600">통계적 방법으로 이상치 및 노이즈 제거</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center mr-4 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">외란 필터링</h4>
                        <p className="text-gray-600">AC, 전압·충격 등 외부 요인 자동 제거</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center mr-4 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">위험도 스코어링</h4>
                        <p className="text-gray-600">잔류침하 및 위험도 정량적 평가</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center mr-4 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">등치선 지도</h4>
                        <p className="text-gray-600">지도 기반 위험도 등치선 표출</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div 
                  className="h-96 bg-cover bg-center rounded-lg shadow-lg"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=AI%20data%20analysis%20dashboard%20for%20construction%20monitoring%2C%20statistical%20filtering%20and%20risk%20scoring%20interface%2C%20purple%20and%20white%20color%20scheme%2C%20advanced%20analytics%20visualization&width=600&height=600&seq=ai-analysis-dashboard&orientation=squarish')`
                  }}
                ></div>
              </div>

              <div className="bg-purple-50 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">API 연동 및 확장성</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-code-line text-white text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">API 연동</h4>
                    <p className="text-gray-600">외부 시스템과의 원활한 데이터 연동</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-robot-line text-white text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">AI 보조 기능</h4>
                    <p className="text-gray-600">지능형 분석 및 예측 기능 제공</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-blue-600 text-white">
            <div className="container mx-auto px-6 text-center">
              <h2 className="text-4xl font-bold mb-6">
                통합 자동화 계측 시스템을 경험해보세요
              </h2>
              <p className="text-xl mb-8 max-w-3xl mx-auto">
                원스톱 통합 솔루션으로 건설 현장의 안전성과 효율성을 한 단계 높여보세요.
                신뢰도 높은 모니터링과 즉시 대응으로 현장 확장성을 극대화합니다.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="#contact" 
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  무료 상담 신청
                </a>
                <a 
                  href="/system" 
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  시스템 구성 보기
                </a>
              </div>
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default OverviewPage;
